import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import { CompetitionTypesEnum, EventStatusEnum } from '../enums';
import { decamelize } from 'humps';
import { DATE_FORMAT, DATE_TIME_FORMAT, MenuItem } from '../constants';
import React from 'react';
import { BingoTicketStatusesEnum, TicketStatusesEnum } from '../enums/administration.enum';
import { DefaultTicketStatus, PassedTicketStatus, TicketType } from '../enums/ticket.enum';

dayjs.extend(utc);
dayjs.extend(isBetween);

const now = +new Date();
let index = 0;

export function uid(prefix = 'uid') {
  return `${prefix}-${now}-${++index}`;
}

export enum LoadingStatusesEnum {
  idle = 'idle',
  pending = 'pending',
  fulfilled = 'fulfilled',
  rejected = 'rejected',
}

export interface LoadingStatuses {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

export const getLoadingStatuses = (status: LoadingStatusesEnum): LoadingStatuses => {
  const isLoading = status === LoadingStatusesEnum.idle || status === LoadingStatusesEnum.pending;
  const isSuccess = status === LoadingStatusesEnum.fulfilled;
  const isError = status === LoadingStatusesEnum.rejected;

  return { isLoading, isSuccess, isError };
};

export const getEnumAsArray = (enumObj: any): { [key: string]: any }[] => {
  return Object.keys(enumObj)
    .filter((key: any) => {
      return isNaN(key);
    })
    .map((name: any) => {
      return {
        name: name,
        id: Number(enumObj[name]),
      };
    });
};

export const getAllSports = (): { [key: string]: any }[] => {
  return getEnumAsArray(CompetitionTypesEnum);
};

export const getAllStatuses = (): { [key: string]: any }[] => {
  return getEnumAsArray(EventStatusEnum);
};

export const getAllTicketStatuses = (): { [key: string]: any }[] => {
  return getEnumAsArray(TicketStatusesEnum);
};

export const getBingoTicketsStatuses = (): { [key: string]: any }[] => {
  return getEnumAsArray(BingoTicketStatusesEnum);
};

export const getDefaultTicketStatuses = (): { [key: string]: any }[] => {
  return getEnumAsArray(DefaultTicketStatus);
};

export const getPassedTicketStatuses = (): { [key: string]: any }[] => {
  return getEnumAsArray(PassedTicketStatus);
};

export const AtmTicketTypes = (): { [key: string]: any }[] => {
  return getEnumAsArray(TicketType);
};

const isoDateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?$/;

export const utcFormat = 'YYYY-MM-DDTHH:mm:ss';

export const handleUtcDates = (body: any) => {
  if (body === null || body === undefined || typeof body !== 'object') return body;

  for (const key of Object.keys(body)) {
    const value = body[key];
    if (isIsoDateString(value)) {
      body[key] = dayjs.utc().format(value);
    } else if (typeof value === 'object') {
      handleUtcDates(value);
    }
  }
};

// export const handledayjsDates = (body: any) => {
//   if (body === null || body === undefined || typeof body !== 'object') return body;
//
//   for (const key of Object.keys(body)) {
//     const value = body[key];
//     if (dayjs.isDayjs(value)) {
//       body[key] = value.utc().format(utcFormat);
//     } else if (typeof value === 'object') {
//       handledayjsDates(value);
//     }
//   }
// };

export const isIsoDateString = (value: any): boolean => {
  return value && typeof value === 'string' && isoDateFormat.test(value);
};

export const capitalizeFirstLetterAllOtherLower = ([first, ...rest]: string, locale = navigator.language) =>
  first.toLocaleUpperCase(locale) + rest.join('').toLocaleLowerCase(locale);

export const capitalizeFirstLetter = ([first, ...rest]: string, locale = navigator.language) =>
  first.toLocaleUpperCase(locale) + rest.join('');

export function arrayToObjectByPropertyName<T>(arr: [], key: string): { [key: string]: T } {
  return arr.reduce((obj, item) => {
    obj[item[key]] = item;
    return obj;
  }, {});
}

/* eslint-disable */
export const isMobile = () => {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        // eslint-disable-line
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        // eslint-disable-line
        a.substr(0, 4),
      )
    )
      check = true;
    // @ts-ignore
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

export const isMobileOrTablet = () => {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        // eslint-disable-line
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        // eslint-disable-line
        a.substr(0, 4),
      )
    )
      check = true;
    // @ts-ignore
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

/* eslint-enable */

export enum DrawerWidthEnum {
  EXTRA_SMALL,
  SMALL,
  MEDIUM,
  LARGE,
  EXTRA_LARGE,
  FULLSCREEN,
}

export const getDrawerWidth = (widthType?: DrawerWidthEnum) => {
  switch (widthType) {
    case DrawerWidthEnum.EXTRA_SMALL:
      return isMobileOrTablet() ? '100vw' : '15vw';
    case DrawerWidthEnum.SMALL:
      return isMobileOrTablet() ? '100vw' : '30vw';
    case DrawerWidthEnum.MEDIUM:
      return isMobileOrTablet() ? '100vw' : '50vw';
    case DrawerWidthEnum.LARGE:
      return isMobileOrTablet() ? '100vw' : '70vw';
    case DrawerWidthEnum.EXTRA_LARGE:
      return isMobileOrTablet() ? '100vw' : '80vw';
    case DrawerWidthEnum.FULLSCREEN:
      return isMobileOrTablet() ? '100vw' : '100vw';
    default:
      return isMobileOrTablet() ? '100vw' : '50vw';
  }
};

export const createFormDataUpload = (values: { [key: string]: any }, file?: any) => {
  const formData = new FormData();

  if (file) {
    formData.append('files', file);
  }
  for (const [key, value] of Object.entries(values)) {
    formData.append(decamelize(key), String(value));
  }

  return formData;
};

export const getAllFormDataAndConvertDateRangeFromTo = (values: any, withSeconds = false) => {
  const filteredObject = Object.entries(values).reduce((res: any, [key, value]) => {
    if (value !== null && value !== undefined && value !== '' && key !== 'dateRange') res[key] = value;
    return res;
  }, {});

  const format = withSeconds ? DATE_TIME_FORMAT : DATE_FORMAT;

  return {
    ...filteredObject,
    fromDate: values.dateRange[0].format(format),
    toDate: values.dateRange[1].format(format),
  };
};

export const isBeginningOfDay = () => {
  const beginningOfDay = dayjs().startOf('day');
  // const endPeriod = dayjs().set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
  const endPeriod = dayjs().set('hour', 8).set('minute', 0).set('second', 0).set('millisecond', 0);
  const currentTime = dayjs();

  return currentTime.isBetween(beginningOfDay, endPeriod);
};

export const getMenuItem = (
  label: React.ReactNode,
  key: React.Key,
  disabled = false,
  icon?: React.ReactNode,
  children?: MenuItem[],
) => {
  return {
    label,
    key,
    disabled,
    icon,
    children,
  };
};

export const removeUndefinedObjectValues = (obj: { [key: string]: any }): { [key: string]: any } => {
  const workObj = { ...obj };
  Object.keys(workObj).forEach((key) => (workObj[key] === undefined ? delete workObj[key] : {}));
  return workObj;
};

export const isEmptyObject = (obj: object | undefined | null): boolean => {
  return !obj || Object.keys(obj).length === 0;
};

export const isSportWithPeriods = (sportId: CompetitionTypesEnum): boolean => {
  const withPeriods = [
    CompetitionTypesEnum.TENNIS,
    CompetitionTypesEnum.BASKETBALL,
    CompetitionTypesEnum.VOLLEYBALL,
    CompetitionTypesEnum.HOCKEY,
    CompetitionTypesEnum.TABLETENNIS,
  ];
  return withPeriods.includes(sportId);
};

export const isSportWithHalfTime = (sportId: CompetitionTypesEnum): boolean => {
  const withPeriods = [
    CompetitionTypesEnum.FOOTBALL,
    CompetitionTypesEnum.BASKETBALL,
    CompetitionTypesEnum.HANDBALL,
    CompetitionTypesEnum.HOCKEY,
  ];
  return withPeriods.includes(sportId);
};

export const syntaxHighlight = (json: any) => {
  if (!json) return ''; //no JSON from response

  json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, // eslint-disable-line
    function (match: any) {
      let cls = 'number';
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'key';
        } else {
          cls = 'string';
        }
      } else if (/true|false/.test(match)) {
        cls = 'boolean';
      } else if (/null/.test(match)) {
        cls = 'null';
      }
      return '<span class="' + cls + '">' + match + '</span>';
    },
  );
};

export const getCookie = (name: string): string | null => {
  const nameLenPlus = name.length + 1;
  return (
    document.cookie
      .split(';')
      .map((c) => c.trim())
      .filter((cookie) => {
        return cookie.substring(0, nameLenPlus) === `${name}=`;
      })
      .map((cookie) => {
        return decodeURIComponent(cookie.substring(nameLenPlus));
      })[0] || null
  );
};

export const getUniqListOfObjects = <T extends Record<string, any>>(
  arrayOfObjects: T[],
  targetPropertyName: keyof T,
): T[] => {
  const uniqueObjects: Record<string, T> = {};

  arrayOfObjects.forEach((obj) => {
    uniqueObjects[obj[targetPropertyName]] = obj;
  });

  return Object.values(uniqueObjects);
};

export const amountFormat = (value: number | undefined, currency: string = 'EUR'): string => {
  if (value === undefined) {
    return '';
  }
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency,
  }).format(value);
};

export const transformObjectToArrayWithModification = <T>(obj: { [key: string]: any }, propertyName: string): T[] => {
  const transformedArray = Object.entries(obj).map(([key, value]) => ({ ...value, [propertyName]: key }));
  return Object.values(transformedArray);
};

export const transformObjectToArrayOfObjects = (obj: any) => {
  return Object.entries(obj).map(([, value]: any) => ({ ...value }));
};
export const countryOptionsISO3166 = [
  { value: 'BA', label: 'Bosnia and Herzegovina' },
  { value: 'ME', label: 'Montenegro' },
  { value: 'RS', label: 'Serbia' },
  { value: 'DE', label: 'Germany' },
  { value: 'SE', label: 'Sweden' },
  { value: 'AW', label: 'Aruba' },
  { value: 'AF', label: 'Afghanistan' },
  { value: 'AO', label: 'Angola' },
  { value: 'AI', label: 'Anguilla' },
  { value: 'AX', label: 'Åland Islands' },
  { value: 'AL', label: 'Albania' },
  { value: 'AD', label: 'Andorra' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'AR', label: 'Argentina' },
  { value: 'AM', label: 'Armenia' },
  { value: 'AS', label: 'American Samoa' },
  { value: 'AQ', label: 'Antarctica' },
  { value: 'TF', label: 'French Southern Territories' },
  { value: 'AG', label: 'Antigua and Barbuda' },
  { value: 'AU', label: 'Australia' },
  { value: 'AT', label: 'Austria' },
  { value: 'AZ', label: 'Azerbaijan' },
  { value: 'BI', label: 'Burundi' },
  { value: 'BE', label: 'Belgium' },
  { value: 'BJ', label: 'Benin' },
  { value: 'BF', label: 'Burkina Faso' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'BG', label: 'Bulgaria' },
  { value: 'BH', label: 'Bahrain' },
  { value: 'BS', label: 'Bahamas' },
  { value: 'BL', label: 'Saint Barthélemy' },
  { value: 'BY', label: 'Belarus' },
  { value: 'BZ', label: 'Belize' },
  { value: 'BM', label: 'Bermuda' },
  { value: 'BR', label: 'Brazil' },
  { value: 'BB', label: 'Barbados' },
  { value: 'BN', label: 'Brunei Darussalam' },
  { value: 'BT', label: 'Bhutan' },
  { value: 'BV', label: 'Bouvet Island' },
  { value: 'BW', label: 'Botswana' },
  { value: 'CF', label: 'Central African Republic' },
  { value: 'CA', label: 'Canada' },
  { value: 'CC', label: 'Cocos (Keeling) Islands' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'CL', label: 'Chile' },
  { value: 'CN', label: 'China' },
  { value: 'CI', label: "Côte d'Ivoire" },
  { value: 'CM', label: 'Cameroon' },
  { value: 'CG', label: 'Congo' },
  { value: 'CK', label: 'Cook Islands' },
  { value: 'CO', label: 'Colombia' },
  { value: 'KM', label: 'Comoros' },
  { value: 'CV', label: 'Cabo Verde' },
  { value: 'CR', label: 'Costa Rica' },
  { value: 'CU', label: 'Cuba' },
  { value: 'CW', label: 'Curaçao' },
  { value: 'CX', label: 'Christmas Island' },
  { value: 'KY', label: 'Cayman Islands' },
  { value: 'CY', label: 'Cyprus' },
  { value: 'CZ', label: 'Czechia' },
  { value: 'DJ', label: 'Djibouti' },
  { value: 'DM', label: 'Dominica' },
  { value: 'DK', label: 'Denmark' },
  { value: 'DO', label: 'Dominican Republic' },
  { value: 'DZ', label: 'Algeria' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'EG', label: 'Egypt' },
  { value: 'ER', label: 'Eritrea' },
  { value: 'EH', label: 'Western Sahara' },
  { value: 'ES', label: 'Spain' },
  { value: 'EE', label: 'Estonia' },
  { value: 'ET', label: 'Ethiopia' },
  { value: 'FI', label: 'Finland' },
  { value: 'FJ', label: 'Fiji' },
  { value: 'FK', label: 'Falkland Islands (Malvinas)' },
  { value: 'FR', label: 'France' },
  { value: 'FO', label: 'Faroe Islands' },
  { value: 'GA', label: 'Gabon' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'GE', label: 'Georgia' },
  { value: 'GG', label: 'Guernsey' },
  { value: 'GH', label: 'Ghana' },
  { value: 'GI', label: 'Gibraltar' },
  { value: 'GN', label: 'Guinea' },
  { value: 'GP', label: 'Guadeloupe' },
  { value: 'GM', label: 'Gambia' },
  { value: 'GW', label: 'Guinea-Bissau' },
  { value: 'GQ', label: 'Equatorial Guinea' },
  { value: 'GR', label: 'Greece' },
  { value: 'GD', label: 'Grenada' },
  { value: 'GL', label: 'Greenland' },
  { value: 'GT', label: 'Guatemala' },
  { value: 'GF', label: 'French Guiana' },
  { value: 'GU', label: 'Guam' },
  { value: 'GY', label: 'Guyana' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'HM', label: 'Heard Island and McDonald Islands' },
  { value: 'HN', label: 'Honduras' },
  { value: 'HR', label: 'Croatia' },
  { value: 'HT', label: 'Haiti' },
  { value: 'HU', label: 'Hungary' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'IM', label: 'Isle of Man' },
  { value: 'IN', label: 'India' },
  { value: 'IO', label: 'British Indian Ocean Territory' },
  { value: 'IE', label: 'Ireland' },
  { value: 'IQ', label: 'Iraq' },
  { value: 'IS', label: 'Iceland' },
  { value: 'IL', label: 'Israel' },
  { value: 'IT', label: 'Italy' },
  { value: 'JM', label: 'Jamaica' },
  { value: 'JE', label: 'Jersey' },
  { value: 'JO', label: 'Jordan' },
  { value: 'JP', label: 'Japan' },
  { value: 'KZ', label: 'Kazakhstan' },
  { value: 'KE', label: 'Kenya' },
  { value: 'KG', label: 'Kyrgyzstan' },
  { value: 'KH', label: 'Cambodia' },
  { value: 'KI', label: 'Kiribati' },
  { value: 'KN', label: 'Saint Kitts and Nevis' },
  { value: 'KW', label: 'Kuwait' },
  { value: 'LA', label: "Lao People's Democratic Republic" },
  { value: 'LB', label: 'Lebanon' },
  { value: 'LR', label: 'Liberia' },
  { value: 'LY', label: 'Libya' },
  { value: 'LC', label: 'Saint Lucia' },
  { value: 'LI', label: 'Liechtenstein' },
  { value: 'LK', label: 'Sri Lanka' },
  { value: 'LS', label: 'Lesotho' },
  { value: 'LT', label: 'Lithuania' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'LV', label: 'Latvia' },
  { value: 'MO', label: 'Macao' },
  { value: 'MF', label: 'Saint Martin (French part)' },
  { value: 'MA', label: 'Morocco' },
  { value: 'MC', label: 'Monaco' },
  { value: 'MG', label: 'Madagascar' },
  { value: 'MV', label: 'Maldives' },
  { value: 'MX', label: 'Mexico' },
  { value: 'MH', label: 'Marshall Islands' },
  { value: 'MK', label: 'North Macedonia' },
  { value: 'ML', label: 'Mali' },
  { value: 'MT', label: 'Malta' },
  { value: 'MM', label: 'Myanmar' },
  { value: 'MN', label: 'Mongolia' },
  { value: 'MP', label: 'Northern Mariana Islands' },
  { value: 'MZ', label: 'Mozambique' },
  { value: 'MR', label: 'Mauritania' },
  { value: 'MS', label: 'Montserrat' },
  { value: 'MQ', label: 'Martinique' },
  { value: 'MU', label: 'Mauritius' },
  { value: 'MW', label: 'Malawi' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'YT', label: 'Mayotte' },
  { value: 'NA', label: 'Namibia' },
  { value: 'NC', label: 'New Caledonia' },
  { value: 'NE', label: 'Niger' },
  { value: 'NF', label: 'Norfolk Island' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'NI', label: 'Nicaragua' },
  { value: 'NU', label: 'Niue' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'NO', label: 'Norway' },
  { value: 'NP', label: 'Nepal' },
  { value: 'NR', label: 'Nauru' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'OM', label: 'Oman' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'PA', label: 'Panama' },
  { value: 'PN', label: 'Pitcairn' },
  { value: 'PE', label: 'Peru' },
  { value: 'PH', label: 'Philippines' },
  { value: 'PW', label: 'Palau' },
  { value: 'PG', label: 'Papua New Guinea' },
  { value: 'PL', label: 'Poland' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'PT', label: 'Portugal' },
  { value: 'PY', label: 'Paraguay' },
  { value: 'PF', label: 'French Polynesia' },
  { value: 'QA', label: 'Qatar' },
  { value: 'RE', label: 'Réunion' },
  { value: 'RO', label: 'Romania' },
  { value: 'RU', label: 'Russian Federation' },
  { value: 'RW', label: 'Rwanda' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'SD', label: 'Sudan' },
  { value: 'SN', label: 'Senegal' },
  { value: 'SG', label: 'Singapore' },
  { value: 'GS', label: 'South Georgia and the South Sandwich Islands' },
  { value: 'SJ', label: 'Svalbard and Jan Mayen' },
  { value: 'SB', label: 'Solomon Islands' },
  { value: 'SL', label: 'Sierra Leone' },
  { value: 'SV', label: 'El Salvador' },
  { value: 'SM', label: 'San Marino' },
  { value: 'SO', label: 'Somalia' },
  { value: 'PM', label: 'Saint Pierre and Miquelon' },
  { value: 'SS', label: 'South Sudan' },
  { value: 'ST', label: 'Sao Tome and Principe' },
  { value: 'SR', label: 'Suriname' },
  { value: 'SK', label: 'Slovakia' },
  { value: 'SI', label: 'Slovenia' },
  { value: 'SZ', label: 'Eswatini' },
  { value: 'SX', label: 'Sint Maarten (Dutch part)' },
  { value: 'SC', label: 'Seychelles' },
  { value: 'SY', label: 'Syrian Arab Republic' },
  { value: 'TC', label: 'Turks and Caicos Islands' },
  { value: 'TD', label: 'Chad' },
  { value: 'TG', label: 'Togo' },
  { value: 'TH', label: 'Thailand' },
  { value: 'TJ', label: 'Tajikistan' },
  { value: 'TK', label: 'Tokelau' },
  { value: 'TM', label: 'Turkmenistan' },
  { value: 'TL', label: 'Timor-Leste' },
  { value: 'TO', label: 'Tonga' },
  { value: 'TT', label: 'Trinidad and Tobago' },
  { value: 'TN', label: 'Tunisia' },
  { value: 'TR', label: 'Turkey' },
  { value: 'TV', label: 'Tuvalu' },
  { value: 'UG', label: 'Uganda' },
  { value: 'UA', label: 'Ukraine' },
  { value: 'UM', label: 'United States Minor Outlying Islands' },
  { value: 'UY', label: 'Uruguay' },
  { value: 'US', label: 'United States' },
  { value: 'UZ', label: 'Uzbekistan' },
  { value: 'VA', label: 'Holy See (Vatican City State)' },
  { value: 'VC', label: 'Saint Vincent and the Grenadines' },
  { value: 'VN', label: 'Viet Nam' },
  { value: 'VU', label: 'Vanuatu' },
  { value: 'WF', label: 'Wallis and Futuna' },
  { value: 'WS', label: 'Samoa' },
  { value: 'YE', label: 'Yemen' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'ZM', label: 'Zambia' },
  { value: 'ZW', label: 'Zimbabwe' },
];
export const countryOptions = [
  { value: 'BIH', label: 'Bosnia and Herzegovina' },
  { value: 'MNE', label: 'Montenegro' },
  { value: 'SRB', label: 'Serbia' },
  { value: 'DEU', label: 'Germany' },
  { value: 'SWE', label: 'Sweden' },

  { value: 'ABW', label: 'Aruba' },
  { value: 'AFG', label: 'Afghanistan' },
  { value: 'AGO', label: 'Angola' },
  { value: 'AIA', label: 'Anguilla' },
  { value: 'ALA', label: 'Åland Islands' },
  { value: 'ALB', label: 'Albania' },
  { value: 'AND', label: 'Andorra' },
  { value: 'ARE', label: 'United Arab Emirates' },
  { value: 'ARG', label: 'Argentina' },
  { value: 'ARM', label: 'Armenia' },
  { value: 'ASM', label: 'American Samoa' },
  { value: 'ATA', label: 'Antarctica' },
  { value: 'ATF', label: 'French Southern Territories' },
  { value: 'ATG', label: 'Antigua and Barbuda' },
  { value: 'AUS', label: 'Australia' },
  { value: 'AUT', label: 'Austria' },
  { value: 'AZE', label: 'Azerbaijan' },
  { value: 'BDI', label: 'Burundi' },
  { value: 'BEL', label: 'Belgium' },
  { value: 'BEN', label: 'Benin' },
  { value: 'BFA', label: 'Burkina Faso' },
  { value: 'BGD', label: 'Bangladesh' },
  { value: 'BGR', label: 'Bulgaria' },
  { value: 'BHR', label: 'Bahrain' },
  { value: 'BHS', label: 'Bahamas' },
  { value: 'BLM', label: 'Saint Barthélemy' },
  { value: 'BLR', label: 'Belarus' },
  { value: 'BLZ', label: 'Belize' },
  { value: 'BMU', label: 'Bermuda' },
  { value: 'BRA', label: 'Brazil' },
  { value: 'BRB', label: 'Barbados' },
  { value: 'BRN', label: 'Brunei Darussalam' },
  { value: 'BTN', label: 'Bhutan' },
  { value: 'BVT', label: 'Bouvet Island' },
  { value: 'BWA', label: 'Botswana' },
  { value: 'CAF', label: 'Central African Republic' },
  { value: 'CAN', label: 'Canada' },
  { value: 'CCK', label: 'Cocos (Keeling) Islands' },
  { value: 'CHE', label: 'Switzerland' },
  { value: 'CHL', label: 'Chile' },
  { value: 'CHN', label: 'China' },
  { value: 'CIV', label: "Côte d'Ivoire" },
  { value: 'CMR', label: 'Cameroon' },
  { value: 'COG', label: 'Congo' },
  { value: 'COK', label: 'Cook Islands' },
  { value: 'COL', label: 'Colombia' },
  { value: 'COM', label: 'Comoros' },
  { value: 'CPV', label: 'Cabo Verde' },
  { value: 'CRI', label: 'Costa Rica' },
  { value: 'CUB', label: 'Cuba' },
  { value: 'CUW', label: 'Curaçao' },
  { value: 'CXR', label: 'Christmas Island' },
  { value: 'CYM', label: 'Cayman Islands' },
  { value: 'CYP', label: 'Cyprus' },
  { value: 'CZE', label: 'Czechia' },
  { value: 'DJI', label: 'Djibouti' },
  { value: 'DMA', label: 'Dominica' },
  { value: 'DNK', label: 'Denmark' },
  { value: 'DOM', label: 'Dominican Republic' },
  { value: 'DZA', label: 'Algeria' },
  { value: 'ECU', label: 'Ecuador' },
  { value: 'EGY', label: 'Egypt' },
  { value: 'ERI', label: 'Eritrea' },
  { value: 'ESH', label: 'Western Sahara' },
  { value: 'ESP', label: 'Spain' },
  { value: 'EST', label: 'Estonia' },
  { value: 'ETH', label: 'Ethiopia' },
  { value: 'FIN', label: 'Finland' },
  { value: 'FJI', label: 'Fiji' },
  { value: 'FLK', label: 'Falkland Islands (Malvinas)' },
  { value: 'FRA', label: 'France' },
  { value: 'FRO', label: 'Faroe Islands' },
  { value: 'GAB', label: 'Gabon' },
  { value: 'GBR', label: 'United Kingdom' },
  { value: 'GEO', label: 'Georgia' },
  { value: 'GGY', label: 'Guernsey' },
  { value: 'GHA', label: 'Ghana' },
  { value: 'GIB', label: 'Gibraltar' },
  { value: 'GIN', label: 'Guinea' },
  { value: 'GLP', label: 'Guadeloupe' },
  { value: 'GMB', label: 'Gambia' },
  { value: 'GNB', label: 'Guinea-Bissau' },
  { value: 'GNQ', label: 'Equatorial Guinea' },
  { value: 'GRC', label: 'Greece' },
  { value: 'GRD', label: 'Grenada' },
  { value: 'GRL', label: 'Greenland' },
  { value: 'GTM', label: 'Guatemala' },
  { value: 'GUF', label: 'French Guiana' },
  { value: 'GUM', label: 'Guam' },
  { value: 'GUY', label: 'Guyana' },
  { value: 'HKG', label: 'Hong Kong' },
  { value: 'HMD', label: 'Heard Island and McDonald Islands' },
  { value: 'HND', label: 'Honduras' },
  { value: 'HRV', label: 'Croatia' },
  { value: 'HTI', label: 'Haiti' },
  { value: 'HUN', label: 'Hungary' },
  { value: 'IDN', label: 'Indonesia' },
  { value: 'IMN', label: 'Isle of Man' },
  { value: 'IND', label: 'India' },
  { value: 'IOT', label: 'British Indian Ocean Territory' },
  { value: 'IRL', label: 'Ireland' },
  { value: 'IRQ', label: 'Iraq' },
  { value: 'ISL', label: 'Iceland' },
  { value: 'ISR', label: 'Israel' },
  { value: 'ITA', label: 'Italy' },
  { value: 'JAM', label: 'Jamaica' },
  { value: 'JEY', label: 'Jersey' },
  { value: 'JOR', label: 'Jordan' },
  { value: 'JPN', label: 'Japan' },
  { value: 'KAZ', label: 'Kazakhstan' },
  { value: 'KEN', label: 'Kenya' },
  { value: 'KGZ', label: 'Kyrgyzstan' },
  { value: 'KHM', label: 'Cambodia' },
  { value: 'KIR', label: 'Kiribati' },
  { value: 'KNA', label: 'Saint Kitts and Nevis' },
  { value: 'KWT', label: 'Kuwait' },
  { value: 'LAO', label: "Lao People's Democratic Republic" },
  { value: 'LBN', label: 'Lebanon' },
  { value: 'LBR', label: 'Liberia' },
  { value: 'LBY', label: 'Libya' },
  { value: 'LCA', label: 'Saint Lucia' },
  { value: 'LIE', label: 'Liechtenstein' },
  { value: 'LKA', label: 'Sri Lanka' },
  { value: 'LSO', label: 'Lesotho' },
  { value: 'LTU', label: 'Lithuania' },
  { value: 'LUX', label: 'Luxembourg' },
  { value: 'LVA', label: 'Latvia' },
  { value: 'MAC', label: 'Macao' },
  { value: 'MAF', label: 'Saint Martin (French part)' },
  { value: 'MAR', label: 'Morocco' },
  { value: 'MCO', label: 'Monaco' },
  { value: 'MDG', label: 'Madagascar' },
  { value: 'MDV', label: 'Maldives' },
  { value: 'MEX', label: 'Mexico' },
  { value: 'MHL', label: 'Marshall Islands' },
  { value: 'MKD', label: 'North Macedonia' },
  { value: 'MLI', label: 'Mali' },
  { value: 'MLT', label: 'Malta' },
  { value: 'MMR', label: 'Myanmar' },
  { value: 'MNG', label: 'Mongolia' },
  { value: 'MNP', label: 'Northern Mariana Islands' },
  { value: 'MOZ', label: 'Mozambique' },
  { value: 'MRT', label: 'Mauritania' },
  { value: 'MSR', label: 'Montserrat' },
  { value: 'MTQ', label: 'Martinique' },
  { value: 'MUS', label: 'Mauritius' },
  { value: 'MWI', label: 'Malawi' },
  { value: 'MYS', label: 'Malaysia' },
  { value: 'MYT', label: 'Mayotte' },
  { value: 'NAM', label: 'Namibia' },
  { value: 'NCL', label: 'New Caledonia' },
  { value: 'NER', label: 'Niger' },
  { value: 'NFK', label: 'Norfolk Island' },
  { value: 'NGA', label: 'Nigeria' },
  { value: 'NIC', label: 'Nicaragua' },
  { value: 'NIU', label: 'Niue' },
  { value: 'NLD', label: 'Netherlands' },
  { value: 'NOR', label: 'Norway' },
  { value: 'NPL', label: 'Nepal' },
  { value: 'NRU', label: 'Nauru' },
  { value: 'NZL', label: 'New Zealand' },
  { value: 'OMN', label: 'Oman' },
  { value: 'PAK', label: 'Pakistan' },
  { value: 'PAN', label: 'Panama' },
  { value: 'PCN', label: 'Pitcairn' },
  { value: 'PER', label: 'Peru' },
  { value: 'PHL', label: 'Philippines' },
  { value: 'PLW', label: 'Palau' },
  { value: 'PNG', label: 'Papua New Guinea' },
  { value: 'POL', label: 'Poland' },
  { value: 'PRI', label: 'Puerto Rico' },
  { value: 'PRT', label: 'Portugal' },
  { value: 'PRY', label: 'Paraguay' },
  { value: 'PYF', label: 'French Polynesia' },
  { value: 'QAT', label: 'Qatar' },
  { value: 'REU', label: 'Réunion' },
  { value: 'ROU', label: 'Romania' },
  { value: 'RUS', label: 'Russian Federation' },
  { value: 'RWA', label: 'Rwanda' },
  { value: 'SAU', label: 'Saudi Arabia' },
  { value: 'SDN', label: 'Sudan' },
  { value: 'SEN', label: 'Senegal' },
  { value: 'SGP', label: 'Singapore' },
  { value: 'SGS', label: 'South Georgia and the South Sandwich Islands' },
  { value: 'SJM', label: 'Svalbard and Jan Mayen' },
  { value: 'SLB', label: 'Solomon Islands' },
  { value: 'SLE', label: 'Sierra Leone' },
  { value: 'SLV', label: 'El Salvador' },
  { value: 'SMR', label: 'San Marino' },
  { value: 'SOM', label: 'Somalia' },
  { value: 'SPM', label: 'Saint Pierre and Miquelon' },
  { value: 'SSD', label: 'South Sudan' },
  { value: 'STP', label: 'Sao Tome and Principe' },
  { value: 'SUR', label: 'Suriname' },
  { value: 'SVK', label: 'Slovakia' },
  { value: 'SVN', label: 'Slovenia' },
  { value: 'SWZ', label: 'Eswatini' },
  { value: 'SXM', label: 'Sint Maarten (Dutch part)' },
  { value: 'SYC', label: 'Seychelles' },
  { value: 'SYR', label: 'Syrian Arab Republic' },
  { value: 'TCA', label: 'Turks and Caicos Islands' },
  { value: 'TCD', label: 'Chad' },
  { value: 'TGO', label: 'Togo' },
  { value: 'THA', label: 'Thailand' },
  { value: 'TJK', label: 'Tajikistan' },
  { value: 'TKL', label: 'Tokelau' },
  { value: 'TKM', label: 'Turkmenistan' },
  { value: 'TLS', label: 'Timor-Leste' },
  { value: 'TON', label: 'Tonga' },
  { value: 'TTO', label: 'Trinidad and Tobago' },
  { value: 'TUN', label: 'Tunisia' },
  { value: 'TUR', label: 'Turkey' },
  { value: 'TUV', label: 'Tuvalu' },
  { value: 'UGA', label: 'Uganda' },
  { value: 'UKR', label: 'Ukraine' },
  { value: 'UMI', label: 'United States Minor Outlying Islands' },
  { value: 'URY', label: 'Uruguay' },
  { value: 'USA', label: 'United States' },
  { value: 'UZB', label: 'Uzbekistan' },
  { value: 'VAT', label: 'Holy See (Vatican City State)' },
  { value: 'VCT', label: 'Saint Vincent and the Grenadines' },
  { value: 'VNM', label: 'Viet Nam' },
  { value: 'VUT', label: 'Vanuatu' },
  { value: 'WLF', label: 'Wallis and Futuna' },
  { value: 'WSM', label: 'Samoa' },
  { value: 'YEM', label: 'Yemen' },
  { value: 'ZAF', label: 'South Africa' },
  { value: 'ZMB', label: 'Zambia' },
  { value: 'ZWE', label: 'Zimbabwe' },
];
export const countryAlpha2Map: Record<string, string> = {
  BIH: 'BA',
  MNE: 'ME',
  SRB: 'RS',
  DEU: 'DE',
  SWE: 'SE',
  ABW: 'AW',
  AFG: 'AF',
  AGO: 'AO',
  AIA: 'AI',
  ALA: 'AX',
  ALB: 'AL',
  AND: 'AD',
  ARE: 'AE',
  ARG: 'AR',
  ARM: 'AM',
  ASM: 'AS',
  ATA: 'AQ',
  ATF: 'TF',
  ATG: 'AG',
  AUS: 'AU',
  AUT: 'AT',
  AZE: 'AZ',
  BDI: 'BI',
  BEL: 'BE',
  BEN: 'BJ',
  BFA: 'BF',
  BGD: 'BD',
  BGR: 'BG',
  BHR: 'BH',
  BHS: 'BS',
  BLM: 'BL',
  BLR: 'BY',
  BLZ: 'BZ',
  BMU: 'BM',
  BRA: 'BR',
  BRB: 'BB',
  BRN: 'BN',
  BTN: 'BT',
  BVT: 'BV',
  BWA: 'BW',
  CAF: 'CF',
  CAN: 'CA',
  CCK: 'CC',
  CHE: 'CH',
  CHL: 'CL',
  CHN: 'CN',
  CIV: 'CI',
  CMR: 'CM',
  COG: 'CG',
  COK: 'CK',
  COL: 'CO',
  COM: 'KM',
  CPV: 'CV',
  CRI: 'CR',
  CUB: 'CU',
  CUW: 'CW',
  CXR: 'CX',
  CYM: 'KY',
  CYP: 'CY',
  CZE: 'CZ',
  DJI: 'DJ',
  DMA: 'DM',
  DNK: 'DK',
  DOM: 'DO',
  DZA: 'DZ',
  ECU: 'EC',
  EGY: 'EG',
  ERI: 'ER',
  ESH: 'EH',
  ESP: 'ES',
  EST: 'EE',
  ETH: 'ET',
  FIN: 'FI',
  FJI: 'FJ',
  FLK: 'FK',
  FRA: 'FR',
  FRO: 'FO',
  GAB: 'GA',
  GBR: 'GB',
  GEO: 'GE',
  GGY: 'GG',
  GHA: 'GH',
  GIB: 'GI',
  GIN: 'GN',
  GLP: 'GP',
  GMB: 'GM',
  GNB: 'GW',
  GNQ: 'GQ',
  GRC: 'GR',
  GRD: 'GD',
  GRL: 'GL',
  GTM: 'GT',
  GUF: 'GF',
  GUM: 'GU',
  GUY: 'GY',
  HKG: 'HK',
  HMD: 'HM',
  HND: 'HN',
  HRV: 'HR',
  HTI: 'HT',
  HUN: 'HU',
  IDN: 'ID',
  IMN: 'IM',
  IND: 'IN',
  IOT: 'IO',
  IRL: 'IE',
  IRQ: 'IQ',
  ISL: 'IS',
  ISR: 'IL',
  ITA: 'IT',
  JAM: 'JM',
  JEY: 'JE',
  JOR: 'JO',
  JPN: 'JP',
  KAZ: 'KZ',
  KEN: 'KE',
  KGZ: 'KG',
  KHM: 'KH',
  KIR: 'KI',
  KNA: 'KN',
  KWT: 'KW',
  LAO: 'LA',
  LBN: 'LB',
  LBR: 'LR',
  LBY: 'LY',
  LCA: 'LC',
  LIE: 'LI',
  LKA: 'LK',
  LSO: 'LS',
  LTU: 'LT',
  LUX: 'LU',
  LVA: 'LV',
  MAC: 'MO',
  MAF: 'MF',
  MAR: 'MA',
  MCO: 'MC',
  MDG: 'MG',
  MDV: 'MV',
  MEX: 'MX',
  MHL: 'MH',
  MKD: 'MK',
  MLI: 'ML',
  MLT: 'MT',
  MMR: 'MM',
  MNG: 'MN',
  MNP: 'MP',
  MOZ: 'MZ',
  MRT: 'MR',
  MSR: 'MS',
  MTQ: 'MQ',
  MUS: 'MU',
  MWI: 'MW',
  MYS: 'MY',
  MYT: 'YT',
  NAM: 'NA',
  NCL: 'NC',
  NER: 'NE',
  NFK: 'NF',
  NGA: 'NG',
  NIC: 'NI',
  NIU: 'NU',
  NLD: 'NL',
  NOR: 'NO',
  NPL: 'NP',
  NRU: 'NR',
  NZL: 'NZ',
  OMN: 'OM',
  PAK: 'PK',
  PAN: 'PA',
  PCN: 'PN',
  PER: 'PE',
  PHL: 'PH',
  PLW: 'PW',
  PNG: 'PG',
  POL: 'PL',
  PRI: 'PR',
  PRT: 'PT',
  PRY: 'PY',
  PYF: 'PF',
  QAT: 'QA',
  REU: 'RE',
  ROU: 'RO',
  RUS: 'RU',
  RWA: 'RW',
  SAU: 'SA',
  SDN: 'SD',
  SEN: 'SN',
  SGP: 'SG',
  SGS: 'GS',
  SJM: 'SJ',
  SLB: 'SB',
  SLE: 'SL',
  SLV: 'SV',
  SMR: 'SM',
  SOM: 'SO',
  SPM: 'PM',
  SSD: 'SS',
  STP: 'ST',
  SUR: 'SR',
  SVK: 'SK',
  SVN: 'SI',
  SWZ: 'SZ',
  SXM: 'SX',
  SYC: 'SC',
  SYR: 'SY',
  TCA: 'TC',
  TCD: 'TD',
  TGO: 'TG',
  THA: 'TH',
  TJK: 'TJ',
  TKL: 'TK',
  TKM: 'TM',
  TLS: 'TL',
  TON: 'TO',
  TTO: 'TT',
  TUN: 'TN',
  TUR: 'TR',
  TUV: 'TV',
  UGA: 'UG',
  UKR: 'UA',
  UMI: 'UM',
  URY: 'UY',
  USA: 'US',
  UZB: 'UZ',
  VAT: 'VA',
  VCT: 'VC',
  VNM: 'VN',
  VUT: 'VU',
  WLF: 'WF',
  WSM: 'WS',
  YEM: 'YE',
  ZAF: 'ZA',
  ZMB: 'ZM',
  ZWE: 'ZW',
};






